import React, { useState } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Divider, InputGroup, InputLeftAddon,
  NumberInput, NumberInputField, useToast, Icon, Link,
} from '@chakra-ui/react';
import { FiFile } from 'react-icons/fi';
import deportesApi, { Articulo, ArchivoGenerico } from '../../../api/deportesApi';
import {
  SectionTitle, Field, useCatalogos, fmtFecha, fmtMoneda, ROJO, ROJO_HOVER, API_UPLOADS,
} from '../_shared';

const esImagenArchivo = (a: ArchivoGenerico) =>
  (a.mimeType || '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes((a.tipoArchivo || '').toLowerCase());

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Articulo;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

export const ESTADOS_FISICOS = ['Nuevo', 'Bueno', 'Regular', 'Malo', 'En mantenimiento', 'Baja'];

const FormularioArticulo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const { categoriasInventario } = useCatalogos();

  const [f, setF] = useState<any>({
    numeroInventario: data?.numeroInventario || '',
    nombre: data?.nombre || '',
    categoriaId: data?.categoriaId ? String(data.categoriaId) : '',
    descripcion: data?.descripcion || '',
    marca: data?.marca || '',
    modelo: data?.modelo || '',
    serie: data?.serie || '',
    cantidad: data?.cantidad ?? 0,
    unidad: data?.unidad || '',
    estadoFisico: data?.estadoFisico || 'Bueno',
    ubicacion: data?.ubicacion || '',
    responsableResguardo: data?.responsableResguardo || '',
    fechaAdquisicion: data?.fechaAdquisicion ? data.fechaAdquisicion.split('T')[0] : '',
    proveedor: data?.proveedor || '',
    costo: data?.costo != null ? String(data.costo) : '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'activo',
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));

  const handleGuardar = async () => {
    if (!f.numeroInventario || !f.nombre || !f.categoriaId) {
      toast({ title: 'Campos obligatorios', description: 'Número de inventario, nombre y categoría.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: any = {
        ...f,
        categoriaId: Number(f.categoriaId),
        costo: f.costo === '' ? null : Number(f.costo),
      };
      if (modo === 'new') payload.cantidad = Number(f.cantidad) || 0;
      else delete payload.cantidad; // PUT no toca existencias
      if (modo === 'edit' && data) await deportesApi.actualizarArticulo(data.id, payload);
      else await deportesApi.crearArticulo(payload);
      toast({ title: modo === 'edit' ? 'Artículo actualizado' : 'Artículo registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo guardar.', status: 'error', duration: 4000 });
    } finally { setGuardando(false); }
  };

  // ────────── VISTA ──────────
  if (modo === 'view') {
    const d = data as Articulo;
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Identificación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Field label="No. INVENTARIO" value={d.numeroInventario} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="NOMBRE" value={d.nombre} /></Box>
          <Field label="CATEGORÍA" value={d.categoria?.nombre} />
          <Field label="MARCA" value={d.marca} />
          <Field label="MODELO" value={d.modelo} />
          <Field label="SERIE" value={d.serie} />
          <Field label="ESTADO FÍSICO" value={<Badge>{d.estadoFisico}</Badge>} />
          <Field label="ESTATUS" value={<Badge colorScheme={d.estatus === 'activo' ? 'green' : 'gray'}>{d.estatus}</Badge>} />
        </SimpleGrid>
        <SectionTitle>Existencias</SectionTitle>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Field label="CANTIDAD TOTAL" value={d.cantidad} />
          <Field label="DISPONIBLE" value={d.cantidadDisponible} />
          <Field label="UNIDAD" value={d.unidad} />
          <Field label="UBICACIÓN" value={d.ubicacion} />
        </SimpleGrid>
        <SectionTitle>Adquisición</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Field label="FECHA ADQUISICIÓN" value={fmtFecha(d.fechaAdquisicion)} />
          <Field label="PROVEEDOR" value={d.proveedor} />
          <Field label="COSTO" value={fmtMoneda(d.costo)} />
          <Field label="RESGUARDO" value={d.responsableResguardo} />
        </SimpleGrid>
        {d.observaciones && (<><SectionTitle>Observaciones</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text></>)}

        <SectionTitle>Fotos</SectionTitle>
        {d.archivos && d.archivos.length > 0 ? (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            {d.archivos.map((a) => (
              <Box key={a.id} borderWidth="1px" borderRadius="md" p={2}>
                {esImagenArchivo(a) ? (
                  <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal title="Abrir foto en pestaña nueva">
                    <img src={`${API_UPLOADS}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
                  </Link>
                ) : (
                  <VStack py={4}><Icon as={FiFile} boxSize={7} color="red.500" /></VStack>
                )}
                <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal fontSize="2xs" color="blue.500" noOfLines={1}>{a.nombreArchivo}</Link>
              </Box>
            ))}
          </SimpleGrid>
        ) : (
          <Text fontSize="sm" color="gray.400">Sin fotos registradas.</Text>
        )}
      </VStack>
    );
  }

  // ────────── NUEVO / EDITAR ──────────
  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Identificación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">No. de inventario</FormLabel>
          <Input value={f.numeroInventario} onChange={(e) => set('numeroInventario', e.target.value)} />
        </FormControl>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre del artículo</FormLabel>
          <Input value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Categoría</FormLabel>
          <Select placeholder="Selecciona" value={f.categoriaId} onChange={(e) => set('categoriaId', e.target.value)}>
            {categoriasInventario.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl><FormLabel fontSize="sm">Marca</FormLabel><Input value={f.marca} onChange={(e) => set('marca', e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Modelo</FormLabel><Input value={f.modelo} onChange={(e) => set('modelo', e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Serie</FormLabel><Input value={f.serie} onChange={(e) => set('serie', e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estado físico</FormLabel>
          <Select value={f.estadoFisico} onChange={(e) => set('estadoFisico', e.target.value)}>
            {ESTADOS_FISICOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        {modo === 'edit' && (
          <FormControl>
            <FormLabel fontSize="sm">Estatus</FormLabel>
            <Select value={f.estatus} onChange={(e) => set('estatus', e.target.value)}>
              <option value="activo">activo</option>
              <option value="inactivo">inactivo</option>
            </Select>
          </FormControl>
        )}
      </SimpleGrid>

      <SectionTitle>Existencias</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {modo === 'new' ? (
          <FormControl>
            <FormLabel fontSize="sm">Cantidad inicial (Alta)</FormLabel>
            <NumberInput min={0} value={f.cantidad} onChange={(v) => set('cantidad', v)}><NumberInputField /></NumberInput>
          </FormControl>
        ) : (
          <FormControl>
            <FormLabel fontSize="sm">Cantidad (por movimientos)</FormLabel>
            <Input isReadOnly bg="gray.50" value={`${data?.cantidad ?? 0} (disp. ${data?.cantidadDisponible ?? 0})`} />
          </FormControl>
        )}
        <FormControl><FormLabel fontSize="sm">Unidad</FormLabel><Input placeholder="pza, kg, par..." value={f.unidad} onChange={(e) => set('unidad', e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Ubicación</FormLabel><Input value={f.ubicacion} onChange={(e) => set('ubicacion', e.target.value)} /></FormControl>
      </SimpleGrid>

      <SectionTitle>Adquisición</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Fecha de adquisición</FormLabel><Input type="date" value={f.fechaAdquisicion} onChange={(e) => set('fechaAdquisicion', e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Proveedor</FormLabel><Input value={f.proveedor} onChange={(e) => set('proveedor', e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Costo</FormLabel>
          <InputGroup><InputLeftAddon>$</InputLeftAddon><Input type="number" min={0} value={f.costo} onChange={(e) => set('costo', e.target.value)} /></InputGroup>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Responsable de resguardo</FormLabel><Input value={f.responsableResguardo} onChange={(e) => set('responsableResguardo', e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 3' }}><FormLabel fontSize="sm">Observaciones</FormLabel><Textarea rows={2} value={f.observaciones} onChange={(e) => set('observaciones', e.target.value)} /></FormControl>
      </SimpleGrid>

      <Divider />
      <HStack justify="flex-end" pt={1} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioArticulo;
